import { useIsMobile } from '@lib/hooks'
import { Toolbar } from '@octopus-do/octopus-storybook'
import {
  CHotkey,
  ChartSimple,
  DollarLight,
  EHotkey,
  IHotkey,
  ImageLight,
  NHotkey,
  NoteStickyLight,
  SHotkey,
} from '@octopus-do/octopus-storybook/icons'
import type { ArtBoardCursorMode } from '@store/reducers/artboard/types'
import type React from 'react'
import { useState } from 'react'

import { createSwitch } from '@/utils'
import {
  ActionsMenu,
  AiMenu,
  AttachFileButton,
  CURSOR_MENU_ID,
  CursorModeMenu,
  EXTERNAL_LINK_ID,
  ExternalLinkPopover,
  TAGS_MENU_ID,
  TagsFilterMenu,
} from './components'
import { CommentsToolbarIcon } from './components/CommentsToolbarIcon'
import {
  COMMENTS_ID,
  ESTIMATE_ID,
  IMAGE_MODE_ID,
  SEO_MODE_ID,
  STICKY_NOTE_ID,
} from './constants'
import { useProjectToolbar } from './hooks'
import css from './styles.module.css'

// Order and grouping follow figma 86165:420742: cursor | AI | editing tools |
// view toggles and the SEO switch.
export const ProjectToolbar: React.FC = () => {
  const {
    actions,
    aiDisabled,
    aiHidden,
    attachDisabled,
    attachLocked,
    attachUploading,
    canEdit,
    commentsActive,
    commentsDisabled,
    cursorMode,
    estimateActive,
    estimateHidden,
    imageActive,
    imageHidden,
    modeLocked,
    seoActive,
    tagsActive,
    tagsDisabled,
    tagsLocked,
    toolsDisabled,
  } = useProjectToolbar()

  // On a phone the three editing actions share one control with a picker
  // (figma 86174:59769).
  const phone = useIsMobile()

  // The menus portal into the bar's own layer so they stay under its plate
  // (their shadow must not fall on the bar); the element exists after mount.
  const [menuLayer, setMenuLayer] = useState<HTMLDivElement | null>(null)

  const switcher = createSwitch()
    // the menu reports ArtBoardCursorMode values as ids
    .case([CURSOR_MENU_ID], (mode) =>
      actions.onCursorMode(mode as ArtBoardCursorMode)
    )
    .case([STICKY_NOTE_ID], actions.onAddStickyNote)
    .case([EXTERNAL_LINK_ID], actions.onAddExternalLink)
    .case([COMMENTS_ID], actions.onToggleComments)
    .case([ESTIMATE_ID], actions.onToggleEstimate)
    .case([IMAGE_MODE_ID], actions.onToggleImageMode)
    .case([SEO_MODE_ID], actions.onToggleSeoMode)
    .case([TAGS_MENU_ID], actions.onToggleTagFilter)

  const onClick = (keys: string[]) => switcher.run(keys)

  const onDefaultCursor = () => actions.onCursorMode('grab')

  return (
    <div className={css.container}>
      <div className={css.menus} ref={setMenuLayer} />
      <Toolbar.Root
        menuContainer={menuLayer}
        onClick={onClick}
        variant="actionbar"
      >
        {/* Groups fold instead of unmounting, so a mode change slides the
            bar rather than snapping it (the legacy bar did the same). */}
        <CursorModeMenu
          mode={cursorMode}
          modeLocked={modeLocked}
          onDefault={onDefaultCursor}
          onExit={actions.onExitMode}
          selectDisabled={!canEdit}
        />

        {/* Reachable in SEO mode on purpose: that is where the menu's only
            item, Generate SEO tags, now lives. */}
        <Toolbar.Collapse shown={!aiHidden}>
          <Toolbar.Divider />
          <AiMenu disabled={aiDisabled} />
        </Toolbar.Collapse>

        <Toolbar.Collapse shown={!modeLocked}>
          <Toolbar.Divider />
          <Toolbar.Collapse shown={canEdit}>
            {phone ? (
              <ActionsMenu
                attachDisabled={attachDisabled}
                attachLocked={attachLocked}
                attachUploading={attachUploading}
                onUpgradeAttachments={actions.onUpgradeAttachments}
                toolsDisabled={toolsDisabled}
              />
            ) : (
              <>
                <Toolbar.Button
                  disabled={toolsDisabled}
                  hotkeys={[NHotkey]}
                  icon={NoteStickyLight}
                  id={STICKY_NOTE_ID}
                  tooltip="Sticky note"
                />
                <AttachFileButton
                  disabled={attachDisabled}
                  loading={attachUploading}
                  locked={attachLocked}
                  onUpgrade={actions.onUpgradeAttachments}
                />
                <ExternalLinkPopover disabled={toolsDisabled} />
              </>
            )}
          </Toolbar.Collapse>
          <Toolbar.Button
            disabled={commentsDisabled}
            hotkeys={[CHotkey]}
            icon={CommentsToolbarIcon}
            id={COMMENTS_ID}
            selected={commentsActive}
            tooltip="Comment"
          />
        </Toolbar.Collapse>

        <Toolbar.Divider />
        <TagsFilterMenu
          disabled={tagsDisabled}
          locked={tagsLocked}
          onUpgrade={actions.onUpgradeTags}
          selected={tagsActive}
        />
        <Toolbar.Collapse shown={!estimateHidden}>
          <Toolbar.Button
            disabled={toolsDisabled}
            hotkeys={[EHotkey]}
            icon={DollarLight}
            id={ESTIMATE_ID}
            selected={estimateActive}
            tooltip="Estimate"
          />
        </Toolbar.Collapse>
        <Toolbar.Collapse shown={!imageHidden}>
          <Toolbar.Button
            disabled={toolsDisabled}
            hotkeys={[IHotkey]}
            icon={ImageLight}
            id={IMAGE_MODE_ID}
            selected={imageActive}
            tooltip="Image mode"
          />
        </Toolbar.Collapse>
        <Toolbar.IconSwitch
          hotkeys={[SHotkey]}
          icon={ChartSimple}
          id={SEO_MODE_ID}
          on={seoActive}
          tooltip="SEO mode"
        />
      </Toolbar.Root>
    </div>
  )
}
